import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { getStoredPartner } from '../lib/partnerContext';

import { getContrastYIQ } from '../lib/colorUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartenaireLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Subdomain passé en param par le logout du dashboard (?sub=manoubia13)
  const subFromUrl = searchParams.get('sub') || getStoredPartner() || null;
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partnerBranding, setPartnerBranding] = useState(null);

  useEffect(() => {
    if (subFromUrl) {
      axios.get(`${API}/partners/by-subdomain/${subFromUrl}`)
        .then(res => setPartnerBranding(res.data))
        .catch(() => {});
    }
  }, [subFromUrl]);

  const brandColor = partnerBranding?.primary_color || '#7C3AED';
  const brandName = partnerBranding?.name || null;
  // Couleur de texte adaptée automatiquement au fond
  const textColor = getContrastYIQ(brandColor);
  const textOpacity70 = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.7)' : 'rgba(31,41,55,0.7)';
  const textOpacity50 = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.5)' : 'rgba(31,41,55,0.5)';
  const cardBg = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const inputBg = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
  const inputBorder = textColor === '#FFFFFF' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.20)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/partenaire/login`, {
        email: formData.email,
        password: formData.password
      });
      
      // Store token and partner data
      localStorage.setItem('partner_token', response.data.access_token);
      localStorage.setItem('partner_data', JSON.stringify(response.data.partner));
      
      toast.success('Connexion réussie !');
      navigate('/partenaire/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Identifiants incorrects';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: brandColor }}>
      {/* Header */}
      <header className="py-6 px-6 border-b" style={{ borderColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(subFromUrl ? `/p/${subFromUrl}` : '/')}
              className="hover:bg-white/10 font-medium"
              style={{ color: textOpacity70 }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            {partnerBranding?.logo_url ? (
              <img src={partnerBranding.logo_url} alt={brandName} className="h-8 w-auto max-w-[140px] object-contain bg-white rounded-lg px-2 py-1" />
            ) : (
              <h1 className="font-heading text-xl font-bold" style={{ color: textColor }}>
                {brandName || "L'ŒIL DU MARCHAND"}
              </h1>
            )}
          </div>
          <span className="font-medium text-sm" style={{ color: textOpacity70 }}>ESPACE PARTENAIRE</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0" style={{ backgroundColor: cardBg, backdropFilter: 'blur(12px)' }}>
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden" style={{ backgroundColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.10)' }}>
              {partnerBranding?.logo_url ? (
                <img src={partnerBranding.logo_url} alt={brandName} className="w-full h-full object-contain p-2" />
              ) : (
                <Building2 className="w-8 h-8" style={{ color: textOpacity70 }} />
              )}
            </div>
            <CardTitle className="text-2xl font-bold" style={{ color: textColor }}>
              {brandName ? `Connexion — ${brandName}` : 'Connexion Partenaire'}
            </CardTitle>
            <p className="text-sm mt-2" style={{ color: textOpacity70 }}>
              Accédez à votre espace Marque Blanche
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label style={{ color: textOpacity70 }}>Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textOpacity50 }} />
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="pl-10"
                    style={{ backgroundColor: inputBg, borderColor: inputBorder, color: textColor }}
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label style={{ color: textOpacity70 }}>Mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textOpacity50 }} />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pl-10 pr-10"
                    style={{ backgroundColor: inputBg, borderColor: inputBorder, color: textColor }}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors z-10"
                    style={{ color: textOpacity70, cursor: 'pointer' }}
                  >
                    {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full font-semibold py-6 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', border: `1px solid ${textColor === '#FFFFFF' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)'}`, color: textColor }}
                data-testid="login-submit"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <div>
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.email) {
                      toast.error('Entrez votre email d\'abord');
                      return;
                    }
                    try {
                      await axios.post(`${API}/reseau/forgot-password`, {
                        email: formData.email,
                        member_type: 'partenaire'
                      });
                      toast.success('Si ce compte existe, un nouveau mot de passe a été envoyé par email.');
                    } catch (e) {
                      toast.success('Si ce compte existe, un nouveau mot de passe a été envoyé par email.');
                    }
                  }}
                  className="hover:underline text-sm"
                  style={{ color: textOpacity70 }}
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <p className="text-sm" style={{ color: textOpacity50 }}>
                Pas encore partenaire ?{' '}
                <button
                  onClick={() => navigate('/reseau/partenaire/inscription')}
                  className="hover:underline font-medium"
                  style={{ color: textOpacity70 }}
                >
                  Devenir partenaire
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-4 px-6 border-t" style={{ borderColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm" style={{ color: textOpacity50 }}>
            {brandName ? `${brandName} · Propulsé par ` : ''}L'Œil du Marchand — Programme Partenaire
          </p>
        </div>
      </footer>
    </div>
  );
}
